import {
  Box,
  Button,
  Divider,
  HStack,
  PopoverBody,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router';
import { ROUTES } from '@investigator/shared/constants';
import { useEntitiesContext, DetectionAlertItem } from '../../context';
import { LogscaleModalSection } from '../EDRDetailsSections';
import { SeverityScore } from '@investigator/shared/components';
import { SkeletonLoader } from '../SkeletonLoader';
import { i18n } from '@investigator/shared/localization';

enum DATA_TEST_IDS {
  DetailsBody = 'entity-information-trigger-details-body',
  DetectionsLabel = 'entity-information-trigger-detections-label',
  DetectionsList = 'entity-information-trigger-detections-list',
  DetectionRow = 'entity-information-trigger-detection-row',
  DetectionBtn = 'entity-information-trigger-detection-btn',
  DetectionLabel = 'entity-information-trigger-detection-label',
  ViewAllBtn = 'entity-information-trigger-view-all-btn',
}

interface EntityPopoverBodyProps {
  detectionsPivotParams: string;
}

export const EntityPopoverBody = ({
  detectionsPivotParams,
}: EntityPopoverBodyProps): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();

  // Get data from context
  const detectionCount = selectedEntity?.detectionData?.detection_count ?? 0;
  const detectionDataList = selectedEntity?.detectionDataList;
  const isLoadingDetections = detectionDataList === undefined;
  const isLoadingLogscale =
    selectedEntity?.logscaleOrigData === undefined ||
    selectedEntity?.logscaleRespData === undefined;

  return (
    <PopoverBody data-testid={DATA_TEST_IDS.DetailsBody}>
      <VStack alignItems="start" w="100%">
        {isLoadingLogscale ? (
          <Box w="100%">
            <SkeletonLoader rowCount={3} />
          </Box>
        ) : (
          <LogscaleModalSection displayCard={true} showHeader={false} />
        )}
        <Text data-testid={DATA_TEST_IDS.DetectionsLabel}>Detections</Text>
        <VStack
          alignItems="start"
          w="100%"
          spacing={0}
          data-testid={DATA_TEST_IDS.DetectionsList}
        >
          {isLoadingDetections ? (
            <Box w="100%">
              <SkeletonLoader rowCount={1} />
            </Box>
          ) : (
            <>
              {detectionDataList &&
                detectionDataList?.length > 0 &&
                detectionDataList
                  ?.slice(0, 3)
                  .map((alert: DetectionAlertItem, idx: number) => (
                    <Box key={alert.alert_name} w="100%">
                      <HStack
                        w="376px"
                        py={1}
                        data-testid={`${DATA_TEST_IDS.DetectionRow}-${idx}`}
                      >
                        <SeverityScore score={alert?.severity} />
                        <Button
                          w="340px"
                          variant="link"
                          colorScheme="gray"
                          as={RouterLink}
                          justifyContent="start"
                          to={{
                            pathname: ROUTES.detections,
                            search: `${detectionsPivotParams}&alert_category=${alert?.alert_name}`,
                          }}
                          data-testid={`${DATA_TEST_IDS.DetectionBtn}-${idx}`}
                          _hover={{
                            textDecoration: 'underline',
                            textDecorationColor: 'text.link',
                          }}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                          }}
                        >
                          <Text
                            as="span"
                            color="text.link"
                            overflow="hidden"
                            isTruncated
                            textOverflow="ellipsis"
                            data-testid={`${DATA_TEST_IDS.DetectionLabel}-${idx}`}
                          >
                            {alert?.alert_name}
                          </Text>
                        </Button>
                      </HStack>
                    </Box>
                  ))}
              {detectionCount > 3 && (
                <Box w="100%" pt={2}>
                  <VStack spacing={3} alignItems="start" w="100%">
                    <Divider />
                    <Button
                      variant="link"
                      colorScheme="gray"
                      as={RouterLink}
                      rightIcon={<ChevronRightIcon color="text.link" />}
                      to={{
                        pathname: ROUTES.detections,
                        search: detectionsPivotParams,
                      }}
                      data-testid={DATA_TEST_IDS.ViewAllBtn}
                      _hover={{
                        textDecoration: 'underline',
                        textDecorationColor: 'text.link',
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                      }}
                    >
                      <Text color="text.link">{i18n.buttons.viewAll}</Text>
                    </Button>
                  </VStack>
                </Box>
              )}
            </>
          )}
        </VStack>
      </VStack>
    </PopoverBody>
  );
};
